import { ApolloError } from '@apollo/client'
import styled from '@emotion/styled'
import { t, Trans } from '@lingui/macro'
import { Form } from 'informed'
import * as React from 'react'

import { validateRequired } from '@emico/ui'

import styles from './CouponForm.module.scss'
import { useApplyCoupon } from '../../../account-dashboard-page/AccountDashboardPageLayout/justRewards/Vouchers/customerVouchers.query'
import FormError from '../../../input/FormError'
import FormGroup from '../../../input/FormGroup'
import Label from '../../../input/Label'
import Submit from '../../../input/Submit'
import { Text } from '../../../input/Text'
import Note from '../../../Note'
import { useSelector } from '../../../types'
import Paragraph from '../../../typography/Text'
import formInteraction from '../../../utils/googleTagManager/formInteraction'
import push from '../../../utils/googleTagManager/push'
import overridable from '../../../utils/overridable'

interface CouponFormValues {
    couponCode: string
}

const StyledNote = styled(Note)`
    margin-top: 15px;
`

const couponFormErrors = {
    cartDoesNotContainProducts: 'Cart does not contain products.',
    invalidCouponCode:
        'This coupon code is not correct. Please check it and try again.',
    minimumPurchaseError: 'This voucher is valid from %1.',
}

const CouponFormNote = overridable('CouponFormNote')(() => (
    <StyledNote>
        <Trans id="cart.couponForm.note">
            Note: Would you like to use a PME Legend gift card? Please redeem it
            at the payment step.
        </Trans>
    </StyledNote>
))

const CouponForm = () => {
    const [error, setError] = React.useState<string>()
    const [success, setSuccess] = React.useState<boolean>(false)
    const couponCode = useSelector(
        (state) => (state.cart.totals && state.cart.totals.coupon_code) || '',
    )
    const applyCoupon = useApplyCoupon()

    const handleSubmit = async (values: CouponFormValues) => {
        try {
            setError(undefined)
            setSuccess(false)

            await applyCoupon(values.couponCode)

            push(
                formInteraction({
                    formName: 'Coupon',
                    formID: 'cart.coupon',
                }),
            )
            setSuccess(true)
        } catch (err) {
            if (err instanceof ApolloError) {
                let error = err.message

                switch (error) {
                    case couponFormErrors.cartDoesNotContainProducts:
                        error = t({
                            id: 'cart.couponForm.cartDoesNotContainProductsError',
                            message: `You can't add a coupon to an empty cart.`,
                        })
                        break
                    case couponFormErrors.invalidCouponCode:
                        error = t({
                            id: 'cart.couponForm.invalidCouponCodeError',
                            message: `This coupon code is not correct. Please check it and try again.`,
                        })
                        break
                    // case couponFormErrors.minimumPurchaseError:
                    //     error = t({
                    //         id: 'cart.couponForm.minimumPurchaseError',
                    //         message: `This voucher is valid from ${err?.json?.parameters?.[0]}.`,
                    //     })
                    //     break
                }

                setError(error)
            } else {
                throw err
            }
        }
    }

    return (
        <>
            {success ? (
                <Paragraph>
                    <Trans id="cart.couponForm.successMessage">
                        The coupon code has been applied.
                    </Trans>
                </Paragraph>
            ) : (
                <Form<CouponFormValues>
                    onSubmit={handleSubmit}
                    initialValues={{ couponCode }}
                    className={styles.base}
                >
                    {error && <FormError>{error}</FormError>}
                    <FormGroup>
                        <Label>
                            <Trans id="cart.couponForm.couponCodeLabel">
                                Promotion code
                            </Trans>
                            <Text
                                field="couponCode"
                                className={styles.input}
                                validate={validateRequired(
                                    t({
                                        id: 'formValidation.couponCodeRequired',
                                        message: `Couponcode is required`,
                                    }),
                                )}
                            />
                        </Label>
                    </FormGroup>

                    <Submit
                        variant="secondary"
                        wide
                        name="Apply code"
                        category="cart.couponForm.submit"
                    >
                        <Trans id="cart.couponForm.submitLabel">
                            Apply code
                        </Trans>
                    </Submit>
                    <CouponFormNote />
                </Form>
            )}
        </>
    )
}

export default overridable('CouponForm')(CouponForm)
